import { useMemo, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Info, Hammer, Package, MonitorSmartphone, Megaphone, FileSignature } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { renderTitle, MXN, toFullUrl } from "@/components/landing/utils";

const ICONS = [Hammer, Package, MonitorSmartphone, Megaphone, FileSignature];

function useCount(target, active, duration = 1200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / duration); setV(target * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return v;
}

function Row({ it, i, active, base, hovered, setHovered }) {
  const Icon = ICONS[i % ICONS.length];
  const pct = Number(it.pct || 0);
  const amount = Number(it.amount) || (base * pct) / 100;
  const shownPct = useCount(pct, active, 1100 + i * 120);
  const shownAmt = useCount(amount, active, 1100 + i * 120);
  const dim = hovered != null && hovered !== i;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }} animate={active ? { opacity: dim ? 0.45 : 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }}
      onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
      className="group rounded-2xl border border-black/6 bg-white p-4 md:p-5 hover:border-[#FFBF57]/60 transition-colors" data-testid={`allocation-item-${i}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center border border-black/5" style={{ background: it.color || "#0A0A0A" }}>
          <Icon size={16} className="text-[#0A0A0A]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm md:text-base font-semibold text-[#0A0A0A]">{it.label}</div>
            <div className="text-right shrink-0">
              <div className="num-mono text-lg md:text-xl text-[#0A0A0A]">{Math.round(shownPct)}%</div>
              {base > 0 && <div className="text-[10px] font-mono text-[#52525B]">{MXN.format(Math.round(shownAmt))}</div>}
            </div>
          </div>
          <div className="mt-2.5 h-2 rounded-full bg-black/[0.05] overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: it.color || "#0A0A0A" }} initial={{ width: 0 }} animate={active ? { width: `${pct}%` } : {}} transition={{ duration: 1.2, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }} />
          </div>
          {it.note && <p className="mt-2.5 text-xs text-[#52525B] leading-relaxed">{it.note}</p>}
        </div>
      </div>
    </motion.div>
  );
}

export default function CapitalAllocationSection({ block }) {
  const items = block?.items || [];
  const base = Number(block?.base_amount) || 0;
  const photo = toFullUrl(block?.image_url);
  const [hovered, setHovered] = useState(null);
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || active) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setActive(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [items.length, active]);
  const total = useMemo(() => items.reduce((s, x) => s + Number(x.pct || 0), 0), [items]);
  const R = 84, C = 2 * Math.PI * R;
  const segs = useMemo(() => { let off = 0; return items.map((it, i) => { const len = (Number(it.pct || 0) / Math.max(total, 1)) * C; const s = { ...it, i, len, off }; off += len; return s; }); }, [items, C, total]);
  const shownBase = useCount(base, active, 1400);
  if (!items.length) return null;
  const focus = hovered != null ? items[hovered] : null;

  return (
    <section id="a-donde-va" ref={ref} className="bg-[#FAFAFA] on-light py-20 md:py-32 relative overflow-hidden" data-testid="allocation-section">
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="max-w-3xl mb-10 md:mb-14">
          <Reveal><span className="eyebrow">{block?.eyebrow || "Transparencia total"}</span></Reveal>
          <Reveal delay={120}><h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.02]">{renderTitle(block?.title || "¿A dónde va cada peso <em>de tu participación</em>?")}</h2></Reveal>
          {block?.subtitle && <Reveal delay={220}><p className="mt-5 text-[#3F3F46] text-base md:text-lg leading-relaxed">{block.subtitle}</p></Reveal>}
        </div>

        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-14 items-start">
          <div className="lg:sticky lg:top-28 space-y-4">
            {photo && (
              <div className="rounded-3xl overflow-hidden aspect-[16/9] relative" data-testid="allocation-photo">
                <img src={photo} alt="Sucursal Rubí Perfumería" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
                <div className="absolute bottom-3 left-4 text-[9px] uppercase tracking-[0.2em] font-mono text-[#FFBF57] bg-[#0A0A0A]/70 px-2.5 py-1 rounded-full">Así se materializa cada apertura</div>
              </div>
            )}
            <div className="rounded-3xl bg-[#0A0A0A] on-onyx text-white p-6 md:p-8 relative overflow-hidden">
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#FFBF57]/15 blur-3xl" />
              <div className="relative flex flex-col items-center">
                <svg viewBox="0 0 200 200" className="w-56 h-56 md:w-64 md:h-64 -rotate-90">
                  <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="18" />
                  {segs.map((s) => (
                    <motion.circle key={s.i} cx="100" cy="100" r={R} fill="none" stroke={s.color || "#FFBF57"} strokeWidth={hovered === s.i ? 24 : 18}
                      strokeDasharray={`${s.len} ${C - s.len}`} initial={{ strokeDashoffset: C }} animate={active ? { strokeDashoffset: -s.off } : {}}
                      transition={{ duration: 1.4, delay: 0.3 + s.i * 0.15, ease: [0.22, 1, 0.36, 1] }} style={{ opacity: hovered != null && hovered !== s.i ? 0.35 : 1, transition: "opacity .3s, stroke-width .3s" }}
                      onMouseEnter={() => setHovered(s.i)} onMouseLeave={() => setHovered(null)} />
                  ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none" style={{ height: "16rem" }}>
                  {focus ? (
                    <><div className="text-4xl num-mono text-[#FFBF57]">{focus.pct}%</div><div className="text-[10px] uppercase tracking-widest font-mono text-white/70 mt-1 max-w-[140px]">{focus.label}</div></>
                  ) : (
                    <><div className="text-[10px] uppercase tracking-widest font-mono text-[#FFBF57]">Presupuesto del proyecto</div><div className="text-3xl md:text-4xl num-mono text-white mt-1" data-testid="allocation-base">{base > 0 ? MXN.format(Math.round(shownBase)) : "100 %"}</div><div className="text-[10px] text-white/50 mt-1">{base > 0 ? "100 % documentado" : "distribuido y documentado"}</div></>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 w-full">
                  {items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-white/80"><span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: it.color }} />{it.label}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-3" data-testid="allocation-list">
            {items.map((it, i) => <Row key={i} it={it} i={i} active={active} base={base} hovered={hovered} setHovered={setHovered} />)}
          </div>
        </div>

        {block?.disclaimer && (
          <Reveal delay={200}>
            <div className="mt-10 flex items-start gap-3 text-[11px] text-[#52525B] leading-relaxed max-w-4xl"><Info size={13} className="text-[#FFBF57] shrink-0 mt-0.5" /><span>{block.disclaimer}</span></div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
